import { useState, useEffect, useRef } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { GeoPiMark } from "@/components/GeoPiLogo";
import { ArrowUp, ArrowDown, Trash2, Pencil, Check, X, GripVertical, Loader2, FileText } from "lucide-react";

export default function GeoPiSectionsManager({ project }) {
  const pid = project.id;
  const [secs, setSecs] = useState(null);
  const [savingOrder, setSavingOrder] = useState(false);
  const [edit, setEdit] = useState(null); // {id, heading, content}
  const [busyId, setBusyId] = useState("");
  const dragFrom = useRef(null);
  const [dragOver, setDragOver] = useState(null);

  const load = () => api.get(`/projects/${pid}/geopi-sections`).then((r) => setSecs(r.data || [])).catch(() => setSecs([]));
  useEffect(() => { load(); }, [pid]);

  const persistOrder = async (ordered) => {
    setSecs(ordered);
    setSavingOrder(true);
    try { await api.put(`/projects/${pid}/geopi-sections/reorder`, { order: ordered.map((s) => s.id) }); }
    catch (e) { toast.error(apiError(e)); load(); }
    finally { setSavingOrder(false); }
  };

  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= secs.length) return;
    const next = [...secs];
    [next[idx], next[j]] = [next[j], next[idx]];
    persistOrder(next);
  };

  const onDrop = (idx) => {
    const from = dragFrom.current;
    setDragOver(null); dragFrom.current = null;
    if (from == null || from === idx) return;
    const next = [...secs];
    const [m] = next.splice(from, 1);
    next.splice(idx, 0, m);
    persistOrder(next);
  };

  const saveEdit = async () => {
    setBusyId(edit.id);
    try {
      await api.put(`/projects/${pid}/geopi-sections/${edit.id}`, { heading: edit.heading || "GeoPi Analysis", content: edit.content || "" });
      setSecs((prev) => prev.map((s) => (s.id === edit.id ? { ...s, heading: edit.heading, content: edit.content } : s)));
      setEdit(null);
      toast.success("Section updated");
    } catch (e) { toast.error(apiError(e)); } finally { setBusyId(""); }
  };

  const del = async (id) => {
    setBusyId(id);
    try { await api.delete(`/projects/${pid}/geopi-sections/${id}`); setSecs((prev) => prev.filter((s) => s.id !== id)); toast.success("Section removed from report"); }
    catch (e) { toast.error(apiError(e)); } finally { setBusyId(""); }
  };

  if (secs === null) return null;

  return (
    <div className="bg-card border border-border rounded-xl p-4" data-testid="geopi-sections-manager">
      <div className="flex items-center gap-2.5 mb-1">
        <span className="h-9 w-9 rounded-md grid place-items-center shrink-0" style={{ background: "#0b1220" }}><GeoPiMark size={20} /></span>
        <div className="mr-auto">
          <div className="text-sm font-bold text-foreground">GeoPi Report Sections</div>
          <div className="text-[11px] text-muted-foreground">Analysis you inserted from GeoPi · appears as a dedicated chapter in the Master Report. Drag or use the arrows to set the order.</div>
        </div>
        {savingOrder && <span className="text-[11px] text-muted-foreground inline-flex items-center gap-1"><Loader2 size={12} className="animate-spin" />Saving order…</span>}
      </div>

      {secs.length === 0 ? (
        <div className="mt-3 border-2 border-dashed border-border rounded-lg py-8 text-center text-muted-foreground" data-testid="geopi-sections-empty">
          <FileText size={30} className="mx-auto mb-2 opacity-60" />
          <p className="text-sm">No GeoPi sections yet.</p>
          <p className="text-[11px] mt-1">Open GeoPi, generate an analysis, then use <span className="font-semibold text-foreground">Insert into report</span> to add it here.</p>
        </div>
      ) : (
        <ul className="mt-3 space-y-2" data-testid="geopi-sections-list">
          {secs.map((s, i) => (
            <li key={s.id} data-testid={`geopi-section-${s.id}`}
              draggable={edit?.id !== s.id}
              onDragStart={() => { dragFrom.current = i; }}
              onDragOver={(e) => { e.preventDefault(); setDragOver(i); }}
              onDragLeave={() => setDragOver((d) => (d === i ? null : d))}
              onDrop={() => onDrop(i)}
              onDragEnd={() => { setDragOver(null); dragFrom.current = null; }}
              className={`rounded-lg border bg-background transition-colors ${dragOver === i ? "border-[#C20511] ring-1 ring-[#C20511]/30" : "border-border"}`}>
              {edit?.id === s.id ? (
                <div className="p-3 space-y-2" data-testid="geopi-section-editor">
                  <input value={edit.heading} onChange={(e) => setEdit({ ...edit, heading: e.target.value })} data-testid="geopi-section-heading-input"
                    className="w-full rounded-md border border-border bg-card px-3 h-9 text-sm font-semibold text-foreground outline-none focus:border-[#C20511]" placeholder="Section heading" />
                  <textarea value={edit.content} onChange={(e) => setEdit({ ...edit, content: e.target.value })} rows={6} data-testid="geopi-section-content-input"
                    className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground outline-none focus:border-[#C20511] resize-y font-mono leading-relaxed" placeholder="Section content (Markdown)" />
                  <div className="flex items-center gap-2 justify-end">
                    <Button variant="outline" size="sm" className="h-8 border-border" onClick={() => setEdit(null)} data-testid="geopi-section-cancel"><X size={14} className="mr-1" />Cancel</Button>
                    <Button size="sm" className="h-8 bg-[#C20511] hover:bg-[#a8040f] text-white" disabled={busyId === s.id} onClick={saveEdit} data-testid="geopi-section-save">
                      {busyId === s.id ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Check size={14} className="mr-1" />}Save
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-2 p-3">
                  <GripVertical size={16} className="text-slate-300 mt-1 shrink-0 cursor-grab" title="Drag to reorder" />
                  <span className="h-6 w-6 rounded-md bg-[#C20511]/10 text-[#C20511] grid place-items-center text-[11px] font-bold shrink-0 mt-0.5">{i + 1}</span>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-bold text-foreground truncate">{s.heading || "GeoPi Analysis"}</div>
                    <div className="geopi-section-preview text-[12px] text-muted-foreground mt-1 line-clamp-3 overflow-hidden">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{(s.content || "").slice(0, 400)}</ReactMarkdown>
                    </div>
                  </div>
                  <div className="flex items-center gap-0.5 shrink-0">
                    <button onClick={() => move(i, -1)} disabled={i === 0 || savingOrder} data-testid={`geopi-section-up-${s.id}`} title="Move up"
                      className="h-7 w-7 grid place-items-center rounded-md text-slate-400 hover:text-[#C20511] hover:bg-slate-100 disabled:opacity-30"><ArrowUp size={15} /></button>
                    <button onClick={() => move(i, 1)} disabled={i === secs.length - 1 || savingOrder} data-testid={`geopi-section-down-${s.id}`} title="Move down"
                      className="h-7 w-7 grid place-items-center rounded-md text-slate-400 hover:text-[#C20511] hover:bg-slate-100 disabled:opacity-30"><ArrowDown size={15} /></button>
                    <button onClick={() => setEdit({ id: s.id, heading: s.heading || "GeoPi Analysis", content: s.content || "" })} data-testid={`geopi-section-edit-${s.id}`} title="Edit"
                      className="h-7 w-7 grid place-items-center rounded-md text-slate-400 hover:text-[#C20511] hover:bg-slate-100"><Pencil size={14} /></button>
                    <button onClick={() => del(s.id)} disabled={busyId === s.id} data-testid={`geopi-section-del-${s.id}`} title="Remove from report"
                      className="h-7 w-7 grid place-items-center rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-40">
                      {busyId === s.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}</button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
